import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, Button, Alert, CircularProgress, Divider, FormControl, InputLabel, Select, MenuItem, Chip
} from '@mui/material';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../config/axios';

// WhatsApp icon component
const WhatsAppIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
  </svg>
);

// Helper function to send WhatsApp message
const sendWhatsAppMessage = (phone, message) => {
  const professionalMessage = `${message}\n\n---\nFrom Team Ishqnama Art\n📞 +91-6393342414\n🌐 www.ishqnamaart.com`;
  const encodedMessage = encodeURIComponent(professionalMessage);
  const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
  window.open(whatsappUrl, "_blank");
};

// Indian States Data
const indianStates = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Lakshadweep', 'Puducherry', 'Andaman and Nicobar Islands'
];

const AddAddress = () => {
  const { orderId } = useParams();
  
  const [formData, setFormData] = useState({
    order_id: orderId,
    recipient_name: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
    phone_number: '',
    email: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [existingAddress, setExistingAddress] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [fetchingAddress, setFetchingAddress] = useState(true);

  // Format status helper
  const formatStatus = (status) => {
    if (!status) return 'N/A';
    return status
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "accepted":
        return { bg: "#e3f2fd", color: "#1565c0" };
      case "in-progress":
        return { bg: "#fff8e1", color: "#f57c00" };
      case "ready-to-ship":
        return { bg: "#e8f5e9", color: "#388e3c" };
      case "dispatched":
        return { bg: "#f3e5f5", color: "#7b1fa2" };
      case "delivered":
        return { bg: "#e0f2f1", color: "#00695c" };
      case "canceled":
        return { bg: "#ffebee", color: "#c62828" };
      default:
        return { bg: "#f5f5f5", color: "#424242" };
    }
  };

  // Fetch existing address and order details on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (!orderId) return;
      
      try {
        setFetchingAddress(true);
        
        // Fetch address and order details in parallel
        const [addressResponse, orderResponse] = await Promise.allSettled([
          api.get(`/get_address.php?order_id=${orderId}`),
          api.get(`/order_status.php?orderId=${orderId}`)
        ]);
        
        // Handle address response
        if (addressResponse.status === 'fulfilled' && addressResponse.value.data.success && addressResponse.value.data.data) {
          setExistingAddress(addressResponse.value.data.data);
        } else {
          setExistingAddress(null);
        }
        
        // Handle order response
        if (orderResponse.status === 'fulfilled' && orderResponse.value.data.success && orderResponse.value.data.data) {
          setOrderDetails(orderResponse.value.data.data);
        } else {
          setOrderDetails(null);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setExistingAddress(null);
        setOrderDetails(null);
      } finally {
        setFetchingAddress(false);
      }
    };

    fetchData();
  }, [orderId]);

  // Validation functions
  const validatePostalCode = (postalCode) => {
    const postalCodeRegex = /^\d{6}$/;
    return postalCodeRegex.test(postalCode);
  };

  const validatePhoneNumber = (phoneNumber) => {
    // Remove any non-digit characters
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(cleanPhone);
  };

  const validateEmail = (email) => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Real-time validation for postal code
    if (field === 'postal_code' && value) {
      if (!validatePostalCode(value)) {
        setValidationErrors(prev => ({
          ...prev,
          postal_code: 'Postal code must be exactly 6 digits'
        }));
      } else {
        setValidationErrors(prev => ({
          ...prev,
          postal_code: ''
        }));
      }
    }

    // Real-time validation for phone number
    if (field === 'phone_number' && value) {
      const cleanPhone = value.replace(/\D/g, '');
      if (!validatePhoneNumber(cleanPhone)) {
        setValidationErrors(prev => ({
          ...prev,
          phone_number: 'Phone number must be 10 digits starting with 6, 7, 8, or 9'
        }));
      } else {
        setValidationErrors(prev => ({
          ...prev,
          phone_number: ''
        }));
      }
    }

    // Real-time validation for email
    if (field === 'email' && value) {
      if (!validateEmail(value)) {
        setValidationErrors(prev => ({
          ...prev,
          email: 'Please enter a valid email address'
        }));
      } else {
        setValidationErrors(prev => ({
          ...prev,
          email: ''
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/create_address.php', {
        ...formData
      });
      if (response.data.success) {
        setSuccess('Address added successfully!');
        // Clear form data
        setFormData({
          order_id: orderId,
          recipient_name: '',
          street_address: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'India',
          phone_number: '',
          email: ''
        });
        // Refetch address after a short delay to show the existing address view
        setTimeout(async () => {
          try {
            const addressResponse = await api.get(`/get_address.php?order_id=${orderId}`);
            if (addressResponse.data.success && addressResponse.data.data) {
              setExistingAddress(addressResponse.data.data);
              setSuccess(''); // Clear success message when showing existing address
            }
          } catch (fetchErr) {
            console.error('Error fetching address after submission:', fetchErr);
          }
        }, 5000); // Show success message for 2 seconds
      } else {
        setError(response.data.error || 'Failed to add address');
      }
    } catch (err) {
      console.error('Error adding address:', err.response?.data?.error);
      setError(err.response?.data?.error || 'Failed to add address. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    const hasValidationErrors = Object.values(validationErrors).some(error => error !== '');
    
    return (
      formData.order_id &&
      formData.street_address &&
      formData.phone_number &&
      formData.state &&
      formData.postal_code &&
      validatePostalCode(formData.postal_code) &&
      validatePhoneNumber(formData.phone_number.replace(/\D/g, '')) &&
      (!formData.email || validateEmail(formData.email)) &&
      !hasValidationErrors
    );
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <>
      <Helmet>
        <title>Delivery Address - Order #{orderId} | Ishqnama Art</title>
        <meta name="description" content={`Manage delivery address for order ${orderId} in Ishqnama Art.`} />
      </Helmet>

      <Box sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: { xs: 2, md: 4 },
        px: { xs: 2, md: 4 },
      }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >


          {/* Loading State */}
          {fetchingAddress && (
            <motion.div variants={itemVariants}>
              <Card sx={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                maxWidth: 800,
                mx: 'auto',
                mt: { xs: 6, md: 6 }
              }}>
                <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Checking address...
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Existing Address Message - Redesigned */}
          {!fetchingAddress && existingAddress && (
            <motion.div variants={itemVariants}>
              <Card sx={{ 
                background: 'white',
                borderRadius: 3,
                maxWidth: 800,
                mx: 'auto',
                mt: { xs: 6, md: 6 },
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <img 
                      src="/pink_logo.png" 
                      alt="Ishqnama Logo" 
                      style={{ 
                        height: 60,
                        objectFit: 'contain'
                      }} 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <img 
                      src="/namelogo.png" 
                      alt="Ishqnama Logo" 
                      style={{ 
                        height: 40,
                        objectFit: 'contain'
                      }} 
                    />
                  </Box>
                  
                  {/* Alert-like Message */}
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mb: 3,
                      borderRadius: 2,
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #90caf9',
                      '& .MuiAlert-icon': {
                        fontSize: '1.5rem',
                        color: '#1976d2'
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.95rem', color: 'text.primary', mt: 1 }}>
                        You have already submitted your delivery address for this order. If you need to make any changes, please contact us on WhatsApp.
                      </Typography>
                  </Alert>

                  {/* Address Details Section */}
                  <Box sx={{ 
                    mb: 3,
                    p: 2.5,
                    borderRadius: 2,
                    border: '2px solid #e0e0e0',
                    backgroundColor: '#fafafa',
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                      Your Submitted Address
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {existingAddress.recipient_name && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            Name
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            {existingAddress.recipient_name}
                          </Typography>
                        </Box>
                      )}

                      {existingAddress.phone_number && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            Phone
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            {existingAddress.phone_number}
                          </Typography>
                        </Box>
                      )}

                      {existingAddress.email && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            Email
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            {existingAddress.email}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ my: 0.5 }} />

                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 0.5 }}>
                          Address
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                          {existingAddress.street_address}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                          City/State/PIN
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                          {[existingAddress.city, existingAddress.state, existingAddress.postal_code]
                            .filter(Boolean)
                            .join(", ")}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                          Country
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                          {existingAddress.country || "India"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* WhatsApp Button */}
                  <Button
                    variant="contained"
                    startIcon={<WhatsAppIcon />}
                    onClick={() => {
                      const message = `Hello, I need to update my delivery address for my order.\n\nCurrent Address:\n${existingAddress.recipient_name ? `Name: ${existingAddress.recipient_name}\n` : ''}${existingAddress.street_address}\n${[existingAddress.city, existingAddress.state, existingAddress.postal_code].filter(Boolean).join(", ")}\n${existingAddress.country || "India"}\n\nPlease help me update this address.`;
                      sendWhatsAppMessage("916393342414", message);
                    }}
                    sx={{
                      background: '#25D366',
                      color: 'white',
                      '&:hover': {
                        background: '#20BA5A',
                      },
                      py: 1.5,
                      px: 3,
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: '1rem',
                      textTransform: 'none',
                    }}
                    fullWidth
                  >
                    Contact on WhatsApp to Update Address
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Success Message - Show when form is submitted successfully */}
          {success && (
            <motion.div variants={itemVariants}>
              <Card sx={{ 
                background: 'white',
                borderRadius: 3,
                maxWidth: 800,
                mx: 'auto',
                mt: { xs: 6, md: 6 },
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}>
                <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <img 
                      src="/pink_logo.png" 
                      alt="Ishqnama Logo" 
                      style={{ 
                        height: 60,
                        objectFit: 'contain'
                      }} 
                    />
                  </Box>
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mb: 2,
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        fontSize: '1.5rem'
                      }
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      Address Submitted Successfully!
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                      Your delivery address has been successfully submitted. We will use this address to deliver your order.
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Error Alert */}
          {error && (
            <motion.div variants={itemVariants}>
              <Alert severity="error" sx={{ mb: 3, maxWidth: 800, mx: 'auto' }}>
                {error}
              </Alert>
            </motion.div>
          )}

          {/* Address Form - Only show if no existing address and no success message */}
          {!fetchingAddress && !existingAddress && !success && (
            <motion.div variants={itemVariants}>
              <Card sx={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                maxWidth: 800,
                mx: 'auto',
                mt: { xs: 6, md: 6 }
              }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <img 
                      src="/pink_logo.png" 
                      alt="Ishqnama Logo" 
                      style={{ 
                        height: 60,
                        objectFit: 'contain'
                      }} 
                    />
                  </Box>
                  
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
                    Add Delivery Address
                  </Typography>
                
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    textAlign: 'center'
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'primary.main',
                      fontWeight: 400,
                      fontSize: '0.95rem'
                    }}
                  >
                    Dear Customer, Your order will be delivered to this address. Please make sure to provide the correct pincode, address, and contact details for successful delivery.
                  </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Recipient Name */}
                  <TextField
                    label="Recipient Name"
                    value={formData.recipient_name}
                    onChange={(e) => handleInputChange('recipient_name', e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />

                  {/* Street Address */}
                  <TextField
                    label="Street Address *"
                    value={formData.street_address}
                    onChange={(e) => handleInputChange('street_address', e.target.value)}
                    fullWidth
                    required
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder="Enter complete street address, apartment/suite number"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />

                  {/* City and State Row */}
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      label="City"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      fullWidth
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                    <FormControl fullWidth required>
                      <InputLabel>State *</InputLabel>
                      <Select
                        value={formData.state}
                        label="State *"
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        sx={{
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        {indianStates.map((state) => (
                          <MenuItem key={state} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Postal Code and Phone Row */}
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      label="Postal Code *"
                      value={formData.postal_code}
                      onChange={(e) => handleInputChange('postal_code', e.target.value)}
                      fullWidth
                      required
                      variant="outlined"
                      placeholder="e.g., 400001"
                      error={!!validationErrors.postal_code}
                      helperText={validationErrors.postal_code}
                      inputProps={{
                        maxLength: 6,
                        pattern: '[0-9]*'
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '0.75rem',
                          margin: '4px 0 0 0'
                        }
                      }}
                    />
                    <TextField
                      label="Phone Number *"
                      value={formData.phone_number}
                      onChange={(e) => handleInputChange('phone_number', e.target.value)}
                      fullWidth
                      required
                      variant="outlined"
                      placeholder="e.g., 9876543210"
                      error={!!validationErrors.phone_number}
                      helperText={validationErrors.phone_number}
                      inputProps={{
                        maxLength: 15,
                        pattern: '[0-9+ -]*'
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: '0.75rem',
                          margin: '4px 0 0 0'
                        }
                      }}
                    />
                  </Box>

                  {/* Email */}
                  <TextField
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    fullWidth
                    variant="outlined"
                    placeholder="e.g., example@email.com"
                    error={!!validationErrors.email}
                    helperText={validationErrors.email}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.75rem',
                        margin: '4px 0 0 0'
                      }
                    }}
                  />

                  {/* Country */}
                  <TextField
                    label="Country"
                    value={formData.country}
                    disabled
                    fullWidth
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(0,0,0,0.04)',
                      },
                    }}
                  />

                  <Divider sx={{ my: 2 }} />

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!isFormValid() || loading}
                    sx={{
                      py: 1.5,
                      px: 4,
                      borderRadius: 2,
                      fontSize: '1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      },
                      '&:disabled': {
                        background: 'rgba(0,0,0,0.12)',
                      }
                    }}
                  >
                    {loading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} color="inherit" />
                        Adding Address...
                      </Box>
                    ) : (
                      'Add Address'
                    )}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
          )}
        </motion.div>
      </Box>
    </>
  );
};

export default AddAddress; 